import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Spinner2 } from 'components';
import { theme } from 'lib';
import { RateItAuth, RateItFeed, RateItHeader, RateItReferralBlocked } from './components';
import useRateItFeed from './hooks/useRateItFeed';
import useRateItReferral from './hooks/useRateItReferral';
import { RATE_IT_COLORS } from './constants';

const Screen = styled.div`
  position: fixed;
  inset: 0;
  background: ${RATE_IT_COLORS.CARD_BG};
  overflow: hidden;
`;

const Centered = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  text-align: center;
  color: #fff;
  font-family: CeraPro, sans-serif;
  font-size: 15px;
`;

const SharedNote = styled.div`
  position: absolute;
  top: 64px;
  left: 12px;
  right: 12px;
  z-index: 12;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(43, 30, 74, 0.92);
  color: #fff;
  font-family: CeraPro, sans-serif;
  font-size: 13px;
  line-height: 18px;
`;

/**
 * The web Rate It landing — the brief's §11 referral destination, and the module's only web
 * surface.
 *
 * Named for the MODULE rather than for the referral, deliberately. The referral is a property of
 * the URL (`?ref=`), not of the screen: this same screen is what a team-channel entry point
 * would open with no referrer at all, and FanCam already owns its own invite flow, so a name
 * like `ReferralScreen` would claim a word that belongs to no single feature.
 *
 * A LAYOUT that stays mounted across the flow's steps, the way `CTAScreenV2/index.js` does: the
 * fetched moments, the rating results and the referral context all have to survive the share
 * step opening and closing, so the steps render inside this component rather than replacing it.
 *
 * `?ref=` carries the link's SHORTCODE rather than the sender's id. That is what lets the banner
 * read the referrer's name and the points figure from one public call, and it is what keeps
 * attribution honest — the activate endpoint reads the referrer from the stored link, so there
 * is nothing here a client could forge.
 */
const RateIt = () => {
  const { t } = useTranslation();
  const { tiriId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);
  const currentUserId = currentUser?._id;

  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shortCode = search.get('ref') || undefined;
  const teamId = search.get('team') || undefined;

  const {
    items,
    sharedItem,
    sharedRating,
    isLoading,
    errorKind,
    hasNextPage,
    isFetchingNextPage,
    loadMore
  } = useRateItFeed({ tiriId, teamId });

  const referralState = useRateItReferral({ shortCode, currentUserId });
  const { activate, isSelfReferral } = referralState;

  const basePath = `/rate-it/${tiriId}`;

  /**
   * Marks this as an active link-entry flow.
   *
   * `User.js` reads `appState.ctaFlowPath` to skip the generic Onboarding screen for a social
   * signup that has no username yet — without it, a fan who signs up mid-flow lands on
   * onboarding instead of back on the moment they were invited to rate. Reusing the existing key
   * rather than adding a second one keeps the gate's meaning ("a link-entry flow is active this
   * session") in one place.
   */
  useEffect(() => {
    dispatch({ type: 'UPDATE_APP_STATE', payload: { ctaFlowPath: basePath } });
  }, [dispatch, basePath]);

  /**
   * Attribute the referral as soon as there is someone to attribute it to.
   *
   * Runs only for a signed-in fan; a guest is attributed after they authenticate. Idempotent
   * server-side, and guarded in the hook, so a re-render cannot post it twice.
   */
  useEffect(() => {
    if (shortCode && currentUserId) activate();
  }, [shortCode, currentUserId, activate]);

  const handleBack = useCallback(() => {
    // `/home` for a signed-in fan, the landing page for a guest — `navigate(-1)` would send a
    // fan who arrived on a deep link back out of the app entirely.
    navigate(currentUserId ? '/home' : '/', { replace: true });
  }, [navigate, currentUserId]);

  /**
   * The tile a visitor pressed before they had an account, replayed once they do.
   *
   * Unreachable on the current flow — a signed-out visitor meets the sign-up screen before the
   * feed, so there is no tile for them to press. Kept because the FEED's gate is kept (see
   * `isSignedIn` below): the two are one mechanism, and half of it is worse than none. Held in
   * component state rather than router state because the `SET_USER` dispatch below swaps the
   * whole container tree and remounts this screen; router state survives a `navigate`, not that.
   */
  const [pendingRating, setPendingRating] = useState(null);
  /**
   * Which form the auth screen shows. NULL means "not chosen", not "no auth screen" — for a
   * signed-out visitor the screen shows regardless and defaults to sign-up. Only `onSwitchMode`
   * ever sets this.
   */
  const [authMode, setAuthMode] = useState(null);

  /**
   * AUTH IS THE FIRST SCREEN for a signed-out visitor.
   *
   * The landing used to open on the feed and gate at the first tile tap — watch freely, sign up
   * to rate. This inverts that: the moment is previewed on the sign-up screen itself, and the
   * feed is what a fan gets after they have an account.
   *
   * Note what this costs, so it is a decision and not a drift: a signed-out visitor can no
   * longer browse, and there is no way out of this screen but to authenticate. If a "look
   * around first" route is ever wanted back, it is this one flag and the feed's gate below —
   * which is why that gate stays even though nothing can reach it today.
   */
  const isSignedIn = !!currentUserId;

  const handleRequireAuth = useCallback((pending) => {
    setPendingRating(pending);
    setAuthMode('signup');
  }, []);

  /**
   * Returns the fan to the shared moment with their account in hand.
   *
   * `SET_USER` swaps `NotUser` for `User` and remounts everything below `containers/Auth.js`, so
   * `navigateTo` is what stops `User.js`'s own mount effect bouncing the fresh login to `/home`.
   * The leading slash is stripped because `User.js` re-adds it; the query string rides along
   * intact, which is what carries `?ref=` through and keeps the referral attributable.
   */
  const handleAuthComplete = useCallback(
    (user) => {
      const target = `rate-it/${tiriId}${location.search}`;
      dispatch({
        type: 'SET_USER',
        payload: currentUserId ? user : { ...user, navigateTo: target }
      });
      setAuthMode(null);
    },
    [dispatch, currentUserId, tiriId, location.search]
  );

  const hasItems = items.length > 0;

  /**
   * Activation has not answered yet, so we do not know whether this link is finished.
   *
   * Folded into the spinner rather than left to resolve underneath, because the feed painting for
   * even one frame is not cosmetic here: the rating bar would be live, and a tap landing in that
   * window records a rating against a referral that is already closed. Only gates when there IS a
   * `?ref=` to resolve — an ordinary Rate It visit waits for nothing.
   */
  const isActivationPending = isSignedIn && !!shortCode && !referralState.activationResolved;

  /**
   * The link is spent. Rendered BEFORE every feed branch and on its own — see
   * `RateItReferralBlocked` for why nothing may show behind it — and it wins over the
   * `errorKind === 'shared'` note below: if the moment is gone AND the invite is finished, the
   * finished invite is the thing the fan needs to be told about.
   *
   * Only reachable once signed in, because `activate` is authed: a signed-out visitor meets
   * `RateItAuth` first and is evaluated on the far side of it, which `handleAuthComplete` carries
   * them back through with `?ref=` intact.
   */
  const isBlocked = isSignedIn && referralState.isCompletedLink;

  if (isBlocked) {
    return (
      <Screen>
        <RateItReferralBlocked
          senderName={referralState.sender?.username || referralState.sender?.name}
          points={referralState.pointsAwarded}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      {isSignedIn && (isLoading || isActivationPending) ? (
        <Centered>
          <Spinner2 col={theme.SECONDARY} />
        </Centered>
      ) : null}

      {isSignedIn && !isLoading && !isActivationPending && errorKind === 'feed' ? (
        <Centered>{t('tiri.rateIt.loadError', "Couldn't load Rate It right now.")}</Centered>
      ) : null}

      {isSignedIn && !isLoading && !isActivationPending && !errorKind && !hasItems ? (
        <Centered>
          {t('tiri.rateIt.noContent', 'No moments to rate right now. Check back soon!')}
        </Centered>
      ) : null}

      {isSignedIn && !isLoading && !isActivationPending && hasItems ? (
        <>
          {/* The link outlived its moment. The referral is still valid and there are other
              moments to rate, so this says so inline rather than dead-ending the landing. */}
          {errorKind === 'shared' ? (
            <SharedNote>
              {t(
                'tiri.rateIt.sharedUnavailable',
                "That moment isn't available any more — here are others to rate."
              )}
            </SharedNote>
          ) : null}

          <RateItFeed
            items={items}
            sharedRating={sharedRating}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            loadMore={loadMore}
            onBack={handleBack}
            // No banner for a fan following their own link, and none without a referral at all —
            // it would promise them a reward that is not on offer.
            referral={shortCode && !isSelfReferral && errorKind !== 'shared' ? referralState : null}
            // Which invite a rating on this landing belongs to. Passed unconditionally, including
            // for a self-referral: the server simply finds no row for it, and a second client-side
            // notion of "is this link live" is how the two drift apart.
            shortCode={shortCode}
            // Always true today — the screen no longer renders this for a guest. The feed keeps
            // its own gate anyway; see `isSignedIn` above.
            isSignedIn={isSignedIn}
            onRequireAuth={handleRequireAuth}
            pendingRating={pendingRating}
            onPendingRatingApplied={() => setPendingRating(null)}
          />
        </>
      ) : null}

      {/* Only for the branches above, where no card exists to render the header and the fan
          would otherwise have no way out. Once the feed mounts, each card renders its own. Not
          for the auth screen, which is opaque, full-bleed and has a shape of its own.

          `isActivationPending` is in here as well as `!hasItems` because the two loads are
          independent: the feed usually resolves first, so a fan waiting on activation can have
          items in hand and still be looking at the spinner — which without this would be a
          screen with no feed, no card and no way back. */}
      {isSignedIn && (!hasItems || isActivationPending) ? (
        <RateItHeader onBack={handleBack} />
      ) : null}

      {/* Signed out: unconditionally. Signed in: only when something asked for it. */}
      {!isSignedIn || authMode ? (
        <RateItAuth
          item={sharedItem || items[0]}
          sender={referralState.sender}
          points={referralState.points}
          isLogin={authMode === 'login'}
          // The preview fills in when the moment arrives; the form never waits on it. A visitor
          // who followed a link should meet the form immediately, not a spinner.
          // Keeps the tile they pressed lit on the preview bar, so the screen answers "what
          // happened to my tap?" without a line of copy. The tap itself is replayed after auth.
          pendingRating={pendingRating?.rating}
          // Carries the query string, so an OAuth round trip comes back to the same moment WITH
          // its referral context rather than to a bare feed.
          returnPath={`/rate-it/${tiriId}${location.search}`}
          onAuthComplete={handleAuthComplete}
          onSwitchMode={() => setAuthMode((mode) => (mode === 'login' ? 'signup' : 'login'))}
        />
      ) : null}
    </Screen>
  );
};

export default RateIt;
